import React, { useEffect, useState, useCallback } from 'react';
import { Container, Typography, Grid, Paper, Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import axios from '../../services/api';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import UpdateIcon from '@mui/icons-material/Update';

interface DashboardStats {
    total_papers: number;
    active_papers: number;
    total_themes: number;
    recent_activity: {
        action: string;
        item: string;
        timestamp: string;
    }[];
}

const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd-MMM-yyyy hh:mm a');
};

const ContentCreatorDashboard: React.FC = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const { t } = useTranslation();

    const fetchStats = useCallback(async () => {
        if (!user || user.role !== 'content_creator') {
            setError(t('contentCreator.dashboard.accessRequired'));
            setLoading(false);
            return;
        }

        try {
            const response = await axios.get('/quiz/dashboard/stats');
            setStats(response.data);
        } catch (error: any) {
            console.error('Failed to fetch dashboard stats:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response headers:', error.response.headers);
            }
            setError(t('contentCreator.dashboard.loadError'));
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!stats) {
        return (
            <Container>
                <Typography color="error">{error || t('contentCreator.dashboard.loadError')}</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    {t('contentCreator.dashboard.title')}
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <DescriptionIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('contentCreator.dashboard.questionPapers')}
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography variant="h4" sx={{ color: 'white' }}>
                                    {stats.total_papers}
                                </Typography>
                                <Typography sx={{ color: 'white' }}>
                                    {t('contentCreator.dashboard.activePapers', { count: stats.active_papers })}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CategoryIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('contentCreator.dashboard.themes')}
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography variant="h4" sx={{ color: 'white' }}>
                                    {stats.total_themes}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <EmojiEmotionsIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('contentCreator.dashboard.welcome')}
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography variant="body1" sx={{ color: 'white' }}>
                                    {t('contentCreator.dashboard.welcomeMessage', { name: user?.first_name })}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper 
                            sx={{ 
                                p: 3,
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <UpdateIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('contentCreator.dashboard.recentActivity')}
                                </Typography>
                            </Box>
                            {stats.recent_activity.map((activity, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        mb: 2,
                                        p: 2,
                                        bgcolor: 'white',
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: 'rgba(255, 255, 255, 0.2)',
                                        '&:hover': {
                                            boxShadow: 2,
                                        }
                                    }}
                                >
                                    <Typography variant="body1" sx={{ color: '#2c3e50' }}>
                                        {activity.action} - {activity.item}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(44, 62, 80, 0.7)' }}>
                                        {formatDate(activity.timestamp)}
                                    </Typography>
                                </Box>
                            ))}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default ContentCreatorDashboard;
